import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Header, Icon, Label, Message, Segment } from 'semantic-ui-react';

import onboardingActions from 'actions/onboarding';
import { useConfirm } from 'shared/confirmation/useConfirm';
import ConfirmModal from 'shared/confirmation/ConfirmModal';
import OnboardingSteps from './subcomponents/OnboardingSteps';
import BusinessProfileStep from './BusinessProfileStep';
import TeamSetupStep from './TeamSetupStep';
import PhoneSystemStep from './PhoneSystemStep';
import CrmConfigurationStep from './CrmConfigurationStep';
import TextingAutomationStep from './TextingAutomationStep';
import ImportIntegrationsStep from './ImportIntegrationsStep';
import { ONBOARDING_STEPS } from './constants';

import './index.scss';

const FIRST_STEP = ONBOARDING_STEPS[0].key;

// Business Profile drives every suggestion and Review & Launch is the finish line, so neither can be skipped
const SKIPPABLE_STEPS = ONBOARDING_STEPS.slice(1, -1).map(({ key }) => key);

// Steps that only make sense when the plan includes the feature (flags come from GetFeatures via planAccess.js)
const STEP_PLAN_FEATURES = {
    phone_system: 'has_phone'
};

// Calls still need somewhere to ring when Team Setup was skipped
const EVERYONE_TEAM = { id: 'everyone', name: 'Everyone' };

const Onboarding = () => {
    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();
    const { user_id, customer_id, teamLimit, plan } = useSelector(state => ({
        user_id: state.auth.user_id,
        customer_id: state.auth.customer_id,
        teamLimit: state.onboarding.plan?.team_limit,
        plan: state.onboarding.plan
    }));

    const savedDraft = useMemo(() => onboardingActions.readDraft(user_id), [user_id]);

    const [currentStep, setCurrentStep] = useState(savedDraft?.current_step || FIRST_STEP);
    const [stepDrafts, setStepDrafts] = useState(savedDraft?.steps || {});
    // Kept apart from step drafts because steps overwrite their own draft on every change
    const [skippedSteps, setSkippedSteps] = useState(savedDraft?.skipped_steps || []);
    const [isShowingRestoredMessage, setIsShowingRestoredMessage] = useState(!!savedDraft);
    // Changing the key remounts the steps so they pick up a cleared draft
    const [resetCount, setResetCount] = useState(0);

    const completedSteps = ONBOARDING_STEPS.filter(({ key }) => !!stepDrafts[key]?.approval).map(({ key }) => key);
    // Until the plan loads every step is shown, so nothing disappears by mistake
    const unavailableSteps = useMemo(() => (
        plan ? Object.keys(STEP_PLAN_FEATURES).filter(key => plan[STEP_PLAN_FEATURES[key]] === false) : []
    ), [plan]);
    const firstIncompleteIndex = ONBOARDING_STEPS.findIndex(({ key }) => !completedSteps.includes(key) && !skippedSteps.includes(key) && !unavailableSteps.includes(key));
    const maxReachableStep = firstIncompleteIndex === -1 ? ONBOARDING_STEPS.length - 1 : firstIncompleteIndex;
    const activeStepIndex = ONBOARDING_STEPS.findIndex(({ key }) => key === currentStep);

    const hasProgress = currentStep !== FIRST_STEP || Object.values(stepDrafts[FIRST_STEP]?.profile || {}).some(value => !!value);

    useEffect(() => {
        if (user_id && hasProgress) {
            onboardingActions.saveDraft(user_id, { current_step: currentStep, steps: stepDrafts, skipped_steps: skippedSteps });
        }
    }, [user_id, hasProgress, currentStep, stepDrafts, skippedSteps]);

    // The plan the customer already bought decides seats and which features each step can offer
    useEffect(() => {
        if (customer_id) {
            dispatch(onboardingActions.readPlanFeatures(customer_id)).catch(() => {});
            dispatch(onboardingActions.readSeats(customer_id)).catch(() => {});
        }
    }, [customer_id, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(onboardingActions.resetOnboarding());
        }
    }, [dispatch]);

    // Stable per-step callbacks so steps can report drafts from effects without looping
    const onBusinessProfileDraftChange = useCallback((draft) => setStepDrafts(drafts => ({ ...drafts, business_profile: draft })), []);
    const onTeamSetupDraftChange = useCallback((draft) => setStepDrafts(drafts => ({ ...drafts, team_setup: draft })), []);
    const onPhoneSystemDraftChange = useCallback((draft) => setStepDrafts(drafts => ({ ...drafts, phone_system: draft })), []);
    const onCrmConfigurationDraftChange = useCallback((draft) => setStepDrafts(drafts => ({ ...drafts, crm_configuration: draft })), []);
    const onTextingAutomationDraftChange = useCallback((draft) => setStepDrafts(drafts => ({ ...drafts, texting_automation: draft })), []);
    const onImportIntegrationsDraftChange = useCallback((draft) => setStepDrafts(drafts => ({ ...drafts, import_integrations: draft })), []);

    const approvedTeams = useMemo(() => {
        const teamSetup = stepDrafts.team_setup;
        const teams = teamSetup?.approval && teamLimit !== 0 ? (teamSetup.teams || []).filter(({ selected }) => selected) : [];
        return teams.length ? teams : [EVERYONE_TEAM];
    }, [stepDrafts.team_setup, teamLimit]);

    const goToStep = (key) => {
        setCurrentStep(key);
        window.scrollTo(0, 0);
    }

    // Moves past steps the plan doesn't include
    const findAvailableStep = useCallback((key, direction) => {
        let index = ONBOARDING_STEPS.findIndex(step => step.key === key) + direction;
        while (ONBOARDING_STEPS[index] && unavailableSteps.includes(ONBOARDING_STEPS[index].key)) {
            index += direction;
        }
        return ONBOARDING_STEPS[index];
    }, [unavailableSteps]);

    const goToNextStep = (key) => {
        const nextStep = findAvailableStep(key, 1);
        if (nextStep) {
            goToStep(nextStep.key);
        }
    }

    const goToPreviousStep = (key) => {
        const previousStep = findAvailableStep(key, -1);
        if (previousStep) {
            goToStep(previousStep.key);
        }
    }

    // A resumed draft can land on a step the plan no longer includes
    const isCurrentStepUnavailable = unavailableSteps.includes(currentStep);
    useEffect(() => {
        if (isCurrentStepUnavailable) {
            const nextStep = findAvailableStep(currentStep, 1);
            if (nextStep) {
                setCurrentStep(nextStep.key);
            }
        }
    }, [isCurrentStepUnavailable, currentStep, findAvailableStep]);

    const completeStep = (key, draft) => {
        setStepDrafts(drafts => ({ ...drafts, [key]: draft }));
        setSkippedSteps(steps => steps.filter(step => step !== key));
        goToNextStep(key);
    }

    const onSkipStep = async () => {
        const { title } = ONBOARDING_STEPS[activeStepIndex];
        const shouldSkip = await isConfirmed(
            `Skip ${title}?`,
            'Anything you entered is kept. You can come back before launch or set this up later in Settings.'
        );
        if (shouldSkip) {
            setSkippedSteps(steps => steps.includes(currentStep) ? steps : [...steps, currentStep]);
            goToNextStep(currentStep);
        }
    }

    const canSkipCurrentStep = SKIPPABLE_STEPS.includes(currentStep) && !completedSteps.includes(currentStep) && !isCurrentStepUnavailable;

    const onStartOver = async () => {
        const shouldStartOver = await isConfirmed('Start over?', 'This clears everything you entered in the onboarding wizard.');
        if (!shouldStartOver) {
            return;
        }
        onboardingActions.clearDraft(user_id);
        setStepDrafts({});
        setSkippedSteps([]);
        setIsShowingRestoredMessage(false);
        setResetCount(count => count + 1);
        goToStep(FIRST_STEP);
    }

    const renderStep = () => {
        switch (currentStep) {
            case 'business_profile':
                return (
                    <BusinessProfileStep
                        key={`business_profile-${resetCount}`}
                        draft={stepDrafts.business_profile}
                        onDraftChange={onBusinessProfileDraftChange}
                        onComplete={(draft) => completeStep('business_profile', draft)}
                    />
                );
            case 'team_setup':
                return (
                    <TeamSetupStep
                        key={`team_setup-${resetCount}`}
                        businessProfile={stepDrafts.business_profile?.profile}
                        draft={stepDrafts.team_setup}
                        onDraftChange={onTeamSetupDraftChange}
                        onBack={() => goToStep('business_profile')}
                        onComplete={(draft) => completeStep('team_setup', draft)}
                        nextStepTitle={findAvailableStep('team_setup', 1)?.title}
                    />
                );
            case 'phone_system':
                return (
                    <PhoneSystemStep
                        key={`phone_system-${resetCount}`}
                        businessProfile={stepDrafts.business_profile?.profile}
                        teams={approvedTeams}
                        draft={stepDrafts.phone_system}
                        onDraftChange={onPhoneSystemDraftChange}
                        onBack={() => goToStep('team_setup')}
                        onComplete={(draft) => completeStep('phone_system', draft)}
                    />
                );
            case 'crm_configuration':
                return (
                    <CrmConfigurationStep
                        key={`crm_configuration-${resetCount}`}
                        businessProfileDraft={stepDrafts.business_profile}
                        draft={stepDrafts.crm_configuration}
                        onDraftChange={onCrmConfigurationDraftChange}
                        onBack={() => goToPreviousStep('crm_configuration')}
                        onComplete={(draft) => completeStep('crm_configuration', draft)}
                    />
                );
            case 'texting_automation':
                return (
                    <TextingAutomationStep
                        key={`texting_automation-${resetCount}`}
                        businessProfileDraft={stepDrafts.business_profile}
                        crmDraft={stepDrafts.crm_configuration}
                        draft={stepDrafts.texting_automation}
                        onDraftChange={onTextingAutomationDraftChange}
                        onBack={() => goToStep('crm_configuration')}
                        onComplete={(draft) => completeStep('texting_automation', draft)}
                    />
                );
            case 'import_integrations':
                return (
                    <ImportIntegrationsStep
                        key={`import_integrations-${resetCount}`}
                        businessProfile={stepDrafts.business_profile?.profile}
                        crmDraft={stepDrafts.crm_configuration}
                        draft={stepDrafts.import_integrations}
                        onDraftChange={onImportIntegrationsDraftChange}
                        onBack={() => goToStep('texting_automation')}
                        onComplete={(draft) => completeStep('import_integrations', draft)}
                    />
                );
            default: {
                const step = ONBOARDING_STEPS[activeStepIndex];
                const previousStep = ONBOARDING_STEPS[activeStepIndex - 1];
                return (
                    <Segment placeholder textAlign='center'>
                        <Header icon>
                            <Icon name='wrench' />
                            {step?.title} is coming soon
                            <Header.Subheader>Everything you've approved so far is saved as a draft.</Header.Subheader>
                        </Header>
                        {previousStep && (
                            <Button basic onClick={() => goToStep(previousStep.key)}>
                                <Icon name='arrow left' /> Back to {previousStep.title}
                            </Button>
                        )}
                    </Segment>
                );
            }
        }
    }

    return (
        <div className='Onboarding'>
            <ConfirmModal />
            <div className='Onboarding__header'>
                <Header as='h2'>
                    Set Up DYL
                    <Header.Subheader>We'll prepare a setup for your business. You review and approve everything before it's applied.</Header.Subheader>
                </Header>
                <div>
                    <Label basic color='purple'><Icon name='flask' /> AI preview: suggestions are simulated</Label>
                    {hasProgress && (
                        <Button basic size='tiny' type='button' onClick={onStartOver}>Start Over</Button>
                    )}
                </div>
            </div>
            <OnboardingSteps
                activeStep={activeStepIndex}
                completedSteps={completedSteps}
                skippedSteps={skippedSteps}
                unavailableSteps={unavailableSteps}
                maxReachableStep={maxReachableStep}
                onStepClick={goToStep}
            />
            {isShowingRestoredMessage && (
                <Message positive size='small' onDismiss={() => setIsShowingRestoredMessage(false)}>
                    Welcome back! We restored your progress from your last visit.
                </Message>
            )}
            {canSkipCurrentStep && (
                <div className='Onboarding__skip'>
                    {skippedSteps.includes(currentStep) && <span>You skipped this step. Approve it to include it in your setup.</span>}
                    <Button basic size='tiny' type='button' onClick={onSkipStep}>
                        Skip for Now <Icon name='angle double right' />
                    </Button>
                </div>
            )}
            {renderStep()}
        </div>
    );
}

export default Onboarding;
